// 052 — Planificación Familiar (Fase 5, Tanda 5, ver PLAN.md). Transcripción
// campo por campo del PDF oficial (páginas 180-181, footer
// `SNS-MSP/HCU-form.052/2021`, 2 hojas, bloques A-L; "N/C = no conoce").
// Nota de fuente: el roadmap original lo llamaba "Gineco-Obstétrico" — el
// nombre real del formulario es Planificación Familiar.
//
// Criterios de siempre: equivalencia funcional (no se dibuja la hoja),
// nada de contenido clínico que el papel no imprime, y solo lo que tiene
// consumidor FHIR real lo crea (un `Condition` por diagnóstico de J — el
// resto queda como `Par[]` de respaldo en el PDF firmado).
//
// El bloque D (controles) es una tabla de hasta 8 visitas (primera + 7
// subsecuentes); se modela con las VISITAS como filas y los datos como
// columnas (transpuesta respecto del papel) para poder tipar cada columna
// (fechas) con `TablaConColumnas`.
import { diagnosticoConMarcaVacio, textoDiagnosticoConMarca, type DiagnosticoConMarca } from './MarcaDiagnostico';
import {
  OPCIONES_NORMAL_ANORMAL,
  OPCIONES_SI_NO,
  OPCIONES_SI_NO_NC,
  MARCAS_PRE_DEF,
  paresDeCampos,
  valoresVacios,
  type CampoSimple,
  type Valores,
} from './camposSimplesDatos';
import type { Par } from './index';
import type { Traducir } from './tecnicaAnestesicaDatos';
import { estadoInicialTablaConColumnas, resumenFilaTablaConColumnas, type ColumnaTablaConColumnas, type EstadoTablaConColumnas, type FilaTablaConColumnas } from './uci/TablaConColumnas';

const sn = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'select', opciones: OPCIONES_SI_NO_NC });
const casilla = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'casilla' });
const numero = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'numero' });
const na = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'select', opciones: OPCIONES_NORMAL_ANORMAL });

// ---------- B. Atención médica ----------

export const CAMPOS_ATENCION_052: readonly CampoSimple[] = [
  { clave: 'informante', etiqueta: 'Nombre del informante' },
  { clave: 'motivoConsulta', etiqueta: 'Motivo de consulta' },
  {
    clave: 'tipoConsulta',
    etiqueta: 'Tipo de consulta',
    tipo: 'select',
    opciones: [
      { valor: 'primera', etiqueta: 'Primera' },
      { valor: 'subsecuente', etiqueta: 'Subsecuente' },
    ],
  },
  // Texto (no `date`): el instructivo permite registrar el tiempo aproximado si no recuerda la fecha exacta.
  { clave: 'fechaUltimaConsulta', etiqueta: 'Fecha de última consulta por planificación familiar (o tiempo aproximado)' },
];

/** Cada hito puberal: edad (años) o "No" (casillero) — desarrollo puberal mujer/hombre. */
export const CAMPOS_PUBERAL_MUJER_052: readonly CampoSimple[] = [
  numero('telarquiaEdad', 'Telarquia — edad'),
  casilla('telarquiaNo', 'Telarquia — No'),
  numero('pubarquiaEdad', 'Pubarquia — edad'),
  casilla('pubarquiaNo', 'Pubarquia — No'),
  numero('menarquiaEdad', 'Menarquia — edad'),
  casilla('menarquiaNo', 'Menarquia — No'),
  numero('velloAxilarEdad', 'Vello axilar — edad'),
  casilla('velloAxilarNo', 'Vello axilar — No'),
];

export const CAMPOS_PUBERAL_HOMBRE_052: readonly CampoSimple[] = [
  numero('pubarquiaEdad', 'Pubarquia — edad'),
  casilla('pubarquiaNo', 'Pubarquia — No'),
  numero('velloAxilarEdad', 'Vello axilar — edad'),
  casilla('velloAxilarNo', 'Vello axilar — No'),
  numero('espermarquiaEdad', 'Espermarquia — edad'),
  casilla('espermarquiaNo', 'Espermarquia — No'),
];

export const CAMPOS_RITMO_MENSTRUAL_052: readonly CampoSimple[] = [
  {
    clave: 'ciclos',
    etiqueta: 'Ciclos',
    tipo: 'select',
    opciones: [
      { valor: 'regular', etiqueta: 'Regular' },
      { valor: 'irregular', etiqueta: 'Irregular' },
    ],
  },
  // El papel imprime DURACIÓN con dos casillas separadas por "/" (el instructivo la pide en días).
  numero('duracionPrimera', 'Duración (días) — 1'),
  numero('duracionSegunda', 'Duración (días) — 2'),
  {
    clave: 'cantidad',
    etiqueta: 'Cantidad',
    tipo: 'select',
    opciones: [
      { valor: 'escaso', etiqueta: 'Escaso' },
      { valor: 'regular', etiqueta: 'Regular' },
      { valor: 'abundante', etiqueta: 'Abundante' },
      { valor: 'nc', etiqueta: 'N/C (no conoce)' },
    ],
  },
  sn('coagulos', 'Coágulos'),
];

export const CAMPOS_DOLOR_052: readonly CampoSimple[] = [
  sn('dolorMenstrualPresente', 'Dolor menstrual'),
  casilla('dolorPremenstrual', 'Dolor menstrual — premenstrual'),
  casilla('dolorMenstrual', 'Dolor menstrual — menstrual'),
  casilla('dolorPostmenstrual', 'Dolor menstrual — postmenstrual'),
  sn('dolorMamario', 'Dolor mamario'),
  sn('mastodinia', 'Mastodinia'),
  sn('otrasMolestias', 'Otras molestias'),
];

export const CAMPOS_DETECCION_CANCER_052: readonly CampoSimple[] = [
  sn('cervicoUterino', 'Cérvico uterino'),
  { clave: 'cervicoUterinoFecha', etiqueta: 'Cérvico uterino — fecha', tipo: 'fecha' },
  sn('mamario', 'Mamario'),
  { clave: 'mamarioFecha', etiqueta: 'Mamario — fecha', tipo: 'fecha' },
  { clave: 'observaciones', etiqueta: 'Observaciones', tipo: 'area' },
];

export const CAMPOS_SEXUALIDAD_052: readonly CampoSimple[] = [
  {
    clave: 'calidadInformacion',
    etiqueta: 'Calidad de la información',
    tipo: 'select',
    opciones: [
      { valor: 'suficiente', etiqueta: 'Suficiente' },
      { valor: 'insuficiente', etiqueta: 'Insuficiente' },
    ],
  },
  sn('parejaActual', 'Pareja actual'),
  numero('tiempoRelacionAnios', 'Tiempo de relación — años'),
  casilla('tiempoRelacionNc', 'Tiempo de relación — N/C'),
  numero('edadPareja', 'Edad de la pareja'),
  casilla('edadParejaNc', 'Edad de la pareja — N/C'),
  numero('numeroParejasSexuales', 'N.º de parejas sexuales'),
  casilla('numeroParejasNc', 'N.º de parejas sexuales — N/C'),
  casilla('experienciaNo', 'Tipo de experiencia sexual — No'),
  casilla('experienciaVaginal', 'Tipo de experiencia sexual — Vaginal'),
  casilla('experienciaOral', 'Tipo de experiencia sexual — Oral'),
  casilla('experienciaAnal', 'Tipo de experiencia sexual — Anal'),
  casilla('experienciaNc', 'Tipo de experiencia sexual — N/C'),
  sn('violenciaSexual', 'Violencia sexual'),
  numero('edadAgresor', 'Edad del agresor'),
  casilla('edadAgresorNc', 'Edad del agresor — N/C'),
  numero('edadOcurrioViolencia', 'Edad en que ocurrió la violencia'),
  casilla('edadOcurrioViolenciaNc', 'Edad en que ocurrió la violencia — N/C'),
  numero('tiempoViolenciaAnios', 'Tiempo de violencia — años (A)'),
  numero('tiempoViolenciaMeses', 'Tiempo de violencia — meses (M)'),
  numero('tiempoViolenciaDias', 'Tiempo de violencia — días (D)'),
  casilla('tiempoViolenciaNc', 'Tiempo de violencia — N/C'),
  sn('tratamientoProfilactico', 'Tratamiento profiláctico'),
  sn('tratamientoPsicologico', 'Tratamiento psicológico'),
  { clave: 'observaciones', etiqueta: 'Observaciones', tipo: 'area' },
];

// ---------- C. Métodos anticonceptivos ----------

export const CAMPOS_EMBARAZO_METODO_052: readonly CampoSimple[] = [
  { clave: 'embarazoConMetodo', etiqueta: 'Embarazo con uso de métodos anticonceptivos', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'embarazoCual', etiqueta: 'Embarazo con uso de métodos — cuál' },
];

export const CAMPOS_HISTORIA_ANTICONCEPTIVA_052: readonly CampoSimple[] = [
  casilla('gestagenoOral', 'Gestágeno oral'),
  casilla('gestagenoDeposito', 'Gestágeno depósito'),
  casilla('diu', 'DIU'),
  casilla('otros', 'Otros'),
  { clave: 'tiempoUso', etiqueta: 'Tiempo de uso' },
  { clave: 'causaAbandono', etiqueta: 'Causa de abandono' },
];

export const CAMPOS_USO_ACTUAL_052: readonly CampoSimple[] = [
  { clave: 'usoActual', etiqueta: 'Uso actual de algún método', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'usoActualCual', etiqueta: 'Uso actual — cuál' },
  { clave: 'usoActualTiempo', etiqueta: 'Uso actual — tiempo de uso' },
  { clave: 'usoActualObservacion', etiqueta: 'Uso actual — observación' },
];

export const CAMPOS_BENEFICIARIO_052: readonly CampoSimple[] = [
  casilla('mujerEdadFertil', 'Beneficiario — mujer en edad fértil'),
  casilla('madreLactante', 'Beneficiario — madre lactante'),
  casilla('hombre', 'Beneficiario — hombre'),
];

export const CAMPOS_ADOPCION_METODO_052: readonly CampoSimple[] = [
  casilla('gestagenoOral', 'Adopción del método actual — gestágeno oral'),
  casilla('gestagenoDeposito', 'Adopción del método actual — gestágeno depósito'),
  casilla('diu', 'Adopción del método actual — DIU'),
  casilla('otros', 'Adopción del método actual — otros'),
  { clave: 'criterioElegibilidad', etiqueta: 'Criterio de elegibilidad' },
  { clave: 'fechaInicio', etiqueta: 'Fecha de inicio', tipo: 'fecha' },
];

// ---------- D. Controles (primera + 7 subsecuentes) ----------

export const FILAS_CONTROLES_052: readonly FilaTablaConColumnas[] = [
  { clave: 'primera', etiqueta: 'Primera' },
  ...Array.from({ length: 7 }, (_, i) => ({ clave: `subsecuente${i + 1}`, etiqueta: `Subsecuente ${i + 1}` })),
];

export const COLUMNAS_CONTROLES_052: readonly ColumnaTablaConColumnas[] = [
  { clave: 'fecha', etiqueta: 'Fecha', tipo: 'fecha' },
  { clave: 'presionArterial', etiqueta: 'Presión arterial' },
  { clave: 'peso', etiqueta: 'Peso (kg)' },
  { clave: 'sangreIntermenstrual', etiqueta: 'Sangre intermenstrual' },
  { clave: 'flujoGenital', etiqueta: 'Flujo genital' },
  { clave: 'mamas', etiqueta: 'Mamas (palpación)' },
  { clave: 'umFecha', etiqueta: 'Última menstruación — fecha', tipo: 'fecha' },
  { clave: 'umDuracion', etiqueta: 'Última menstruación — duración' },
  { clave: 'umCantidad', etiqueta: 'Última menstruación — cantidad' },
  { clave: 'diuHilo', etiqueta: 'Evolución DIU — hilo' },
  { clave: 'diuExpulsion', etiqueta: 'Evolución DIU — expulsión' },
  { clave: 'diuRetiro', etiqueta: 'Evolución DIU — retiro' },
  { clave: 'diuReinsercion', etiqueta: 'Evolución DIU — reinserción' },
  { clave: 'abandonoSalud', etiqueta: 'Causa abandono — salud' },
  { clave: 'abandonoPersonales', etiqueta: 'Causa abandono — personales' },
  { clave: 'abandonoDeseaHijo', etiqueta: 'Causa abandono — desea hijo' },
  { clave: 'abandonoEmbarazo', etiqueta: 'Causa abandono — embarazo' },
  { clave: 'molestiasSecundarias', etiqueta: 'Molestias secundarias' },
  { clave: 'cambioMetodo', etiqueta: 'Cambio de método' },
  { clave: 'nombreExaminador', etiqueta: 'Nombre del examinador' },
];

// ---------- E. ITS / F. Fecundidad ----------

export const CAMPOS_ITS_052: readonly CampoSimple[] = [
  { clave: 'tieneAlguna', etiqueta: 'Tiene o ha tenido alguna ITS', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'cual', etiqueta: 'ITS — cuál' },
  { clave: 'tratamiento', etiqueta: 'Tratamiento', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'tratamientoCual', etiqueta: 'Tratamiento — cuál' },
  { clave: 'observaciones', etiqueta: 'Observaciones', tipo: 'area' },
];

export const CAMPOS_FECUNDIDAD_052: readonly CampoSimple[] = [
  numero('embarazos', 'Número de embarazos'),
  numero('partos', 'Número de partos'),
  numero('cesareas', 'Número de cesáreas'),
  numero('abortos', 'Número de abortos'),
  numero('ectopicos', 'Embarazos ectópicos'),
  numero('nacidosVivos', 'Número de nacidos vivos'),
  numero('nacidosMuertos', 'Número de nacidos muertos'),
  { clave: 'fechaUltimaGestacion', etiqueta: 'Fecha de última gestación', tipo: 'fecha' },
  { clave: 'lactanciaAnterior', etiqueta: 'Lactancia anterior', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'lactanciaActual', etiqueta: 'Lactancia actual', tipo: 'select', opciones: OPCIONES_SI_NO },
  numero('lactanciaMaximaMeses', 'Lactancia máxima en meses'),
];

// ---------- G / H. Examen físico de genitales ----------

export const CAMPOS_EXAMEN_FEMENINO_052: readonly CampoSimple[] = [
  numero('tannerMamas', 'Tanner — mamas'),
  numero('tannerVello', 'Tanner — vello'),
  na('examenMamario', 'Examen mamario'),
  na('vulva', 'Vulva'),
  na('clitoris', 'Clítoris'),
  na('vagina', 'Vagina'),
  na('cuelloUterino', 'Cuello uterino'),
  { clave: 'observaciones', etiqueta: 'Observaciones', tipo: 'area' },
];

export const CAMPOS_EXAMEN_MASCULINO_052: readonly CampoSimple[] = [
  numero('tannerGenitales', 'Tanner — genitales'),
  numero('tannerVello', 'Tanner — vello'),
  na('examenMamario', 'Examen mamario'),
  na('cuerpoPene', 'Cuerpo del pene'),
  na('glande', 'Glande'),
  na('prepucio', 'Prepucio'),
  na('escroto', 'Escroto'),
  na('testiculoDerecho', 'Testículo derecho'),
  na('testiculoIzquierdo', 'Testículo izquierdo'),
  { clave: 'observaciones', etiqueta: 'Observaciones', tipo: 'area' },
];

// ---------- I. Exámenes complementarios ----------

export const CAMPOS_COMPLEMENTARIOS_052: readonly CampoSimple[] = [
  casilla('hemograma', 'Hemograma'),
  casilla('glicemia', 'Glicemia'),
  casilla('perfilHormonal', 'Perfil hormonal'),
  casilla('emo', 'EMO'),
  casilla('torch', 'TORCH'),
  casilla('vdrl', 'VDRL'),
  casilla('hiv', 'HIV'),
  casilla('hepatitis', 'Hepatitis'),
  casilla('hpv', 'HPV'),
  casilla('citologiaVaginal', 'Citología vaginal'),
  casilla('colposcopia', 'Colposcopia'),
  casilla('eco', 'ECO'),
  casilla('mamografia', 'Mamografía'),
  casilla('otros', 'Otros'),
  { clave: 'otrosDetalle', etiqueta: 'Otros — detalle' },
];

// ---------- J / K / L ----------

export const NUM_DIAGNOSTICOS_052 = 6;

export const CAMPOS_PLANES_052: readonly CampoSimple[] = [
  { clave: 'planes', etiqueta: 'Planes: diagnóstico, terapéutico y educacional', tipo: 'area' },
  { clave: 'carnetCema', etiqueta: 'Carnet CEMA', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'observaciones', etiqueta: 'Observaciones', tipo: 'area' },
];

// ---------- Estado ----------

export interface EstadoBitacora052 {
  readonly atencion: Valores;
  readonly puberalMujer: Valores;
  readonly puberalHombre: Valores;
  readonly ritmoMenstrual: Valores;
  readonly dolor: Valores;
  readonly deteccionCancer: Valores;
  readonly sexualidad: Valores;
  readonly embarazoMetodo: Valores;
  readonly historiaAnticonceptiva: Valores;
  readonly usoActual: Valores;
  readonly beneficiario: Valores;
  readonly adopcionMetodo: Valores;
  readonly controles: EstadoTablaConColumnas;
  readonly its: Valores;
  readonly fecundidad: Valores;
  readonly examenFemenino: Valores;
  readonly examenMasculino: Valores;
  readonly complementarios: Valores;
  readonly diagnosticos: readonly DiagnosticoConMarca[];
  readonly planes: Valores;
}

export function estadoInicialBitacora052(): EstadoBitacora052 {
  return {
    atencion: valoresVacios(CAMPOS_ATENCION_052),
    puberalMujer: valoresVacios(CAMPOS_PUBERAL_MUJER_052),
    puberalHombre: valoresVacios(CAMPOS_PUBERAL_HOMBRE_052),
    ritmoMenstrual: valoresVacios(CAMPOS_RITMO_MENSTRUAL_052),
    dolor: valoresVacios(CAMPOS_DOLOR_052),
    deteccionCancer: valoresVacios(CAMPOS_DETECCION_CANCER_052),
    sexualidad: valoresVacios(CAMPOS_SEXUALIDAD_052),
    embarazoMetodo: valoresVacios(CAMPOS_EMBARAZO_METODO_052),
    historiaAnticonceptiva: valoresVacios(CAMPOS_HISTORIA_ANTICONCEPTIVA_052),
    usoActual: valoresVacios(CAMPOS_USO_ACTUAL_052),
    beneficiario: valoresVacios(CAMPOS_BENEFICIARIO_052),
    adopcionMetodo: valoresVacios(CAMPOS_ADOPCION_METODO_052),
    controles: estadoInicialTablaConColumnas(FILAS_CONTROLES_052, COLUMNAS_CONTROLES_052),
    its: valoresVacios(CAMPOS_ITS_052),
    fecundidad: valoresVacios(CAMPOS_FECUNDIDAD_052),
    examenFemenino: valoresVacios(CAMPOS_EXAMEN_FEMENINO_052),
    examenMasculino: valoresVacios(CAMPOS_EXAMEN_MASCULINO_052),
    complementarios: valoresVacios(CAMPOS_COMPLEMENTARIOS_052),
    diagnosticos: Array.from({ length: NUM_DIAGNOSTICOS_052 }, () => diagnosticoConMarcaVacio()),
    planes: valoresVacios(CAMPOS_PLANES_052),
  };
}

/** Serializa toda la historia de 052 a `Par[]`, listos para `onGuardado` — filtra respuestas vacías. */
export function paresDeBitacora052(t: Traducir, e: EstadoBitacora052): Par[] {
  const p = (clave: string, campos: readonly CampoSimple[], valores: Valores): Par[] => paresDeCampos(campos, valores, `${t(`formularios.f052.${clave}`)} — `);
  const pares: Par[] = [
    ...paresDeCampos(CAMPOS_ATENCION_052, e.atencion),
    ...p('puberalMujerTitulo', CAMPOS_PUBERAL_MUJER_052, e.puberalMujer),
    ...p('puberalHombreTitulo', CAMPOS_PUBERAL_HOMBRE_052, e.puberalHombre),
    ...p('ritmoMenstrualTitulo', CAMPOS_RITMO_MENSTRUAL_052, e.ritmoMenstrual),
    ...paresDeCampos(CAMPOS_DOLOR_052, e.dolor),
    ...p('deteccionCancerTitulo', CAMPOS_DETECCION_CANCER_052, e.deteccionCancer),
    ...p('sexualidadTitulo', CAMPOS_SEXUALIDAD_052, e.sexualidad),
    ...paresDeCampos(CAMPOS_EMBARAZO_METODO_052, e.embarazoMetodo),
    ...p('historiaAnticonceptivaTitulo', CAMPOS_HISTORIA_ANTICONCEPTIVA_052, e.historiaAnticonceptiva),
    ...paresDeCampos(CAMPOS_USO_ACTUAL_052, e.usoActual),
    ...paresDeCampos(CAMPOS_BENEFICIARIO_052, e.beneficiario),
    ...paresDeCampos(CAMPOS_ADOPCION_METODO_052, e.adopcionMetodo),
    ...FILAS_CONTROLES_052.map((f) => ({ pregunta: `${t('formularios.f052.controlesTitulo')} — ${f.etiqueta}`, respuesta: resumenFilaTablaConColumnas(e.controles, f.clave, COLUMNAS_CONTROLES_052) })),
    ...p('itsTitulo', CAMPOS_ITS_052, e.its),
    ...p('fecundidadTitulo', CAMPOS_FECUNDIDAD_052, e.fecundidad),
    ...p('examenFemeninoTitulo', CAMPOS_EXAMEN_FEMENINO_052, e.examenFemenino),
    ...p('examenMasculinoTitulo', CAMPOS_EXAMEN_MASCULINO_052, e.examenMasculino),
    ...p('complementariosTitulo', CAMPOS_COMPLEMENTARIOS_052, e.complementarios),
    ...e.diagnosticos.map((d, i) => ({
      pregunta: `${t('formularios.f052.diagnosticoTitulo')} — ${i + 1}`,
      respuesta: textoDiagnosticoConMarca(d, MARCAS_PRE_DEF),
    })),
    ...paresDeCampos(CAMPOS_PLANES_052, e.planes),
  ];
  return pares.filter((x) => x.respuesta.trim() !== '');
}
